import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, Loader2, Download, ExternalLink, Sparkles, Save } from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const LABELS = {
  company_name_address: "Offeror name & address", cage: "CAGE code", facility_code: "Facility code",
  remittance_address: "Remittance address", accept_days: "Acceptance period (days)",
  offer_amount_line: "Offer amount", signer_name_title: "Signer name & title",
};

export default function Forms({ embedded = false }) {
  const [deals, setDeals] = useState([]);
  const [dealId, setDealId] = useState("");
  const [keys, setKeys] = useState([]);
  const [values, setValues] = useState({});
  const [aiKeys, setAiKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get("/pipeline?bid_type=Federal").then((r) => setDeals(r.data.deals || [])).catch(() => {});
    api.get("/forms").then((r) => setKeys(r.data.forms?.[0]?.keys || [])).catch(() => {});
  }, []);

  const aiMap = async () => {
    if (!dealId) { toast.error("Pick a federal deal first."); return; }
    setBusy(true); setReady(false);
    try {
      const r = await api.post(`/bids/${dealId}/forms/sf1442/ai-map`);
      setValues(r.data.values || {}); setAiKeys(r.data.ai_keys || []);
      toast.success(`Anvil auto-mapped ${(r.data.ai_keys || []).length} field(s).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI map failed"); }
    finally { setBusy(false); }
  };

  const saveAndPreview = async () => {
    setBusy(true);
    try {
      await api.post(`/bids/${dealId}/forms/sf1442/values`, { values });
      setReady(true); toast.success("Saved. Filled SF-1442 ready.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const visKeys = (keys.length ? keys : Object.keys(values)).filter((k) => !k.startsWith("amend_"));
  const pdfUrl = `${API_BASE}/bids/${dealId}/forms/sf1442.pdf`;

  return (
    <div className={embedded ? "" : "p-5 sm:p-8 max-w-[1500px] mx-auto"} data-testid="forms-page">
      {!embedded && (
        <>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900 flex items-center gap-2.5">
            <FileSignature className="h-7 w-7" /> AI Form Fill
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Anvil auto-maps Williams Diversified's data + the bid into the official fillable SF-1442 (real AcroForm fill + automapping). Also baked into each bid's proposal PDF.</p>
        </>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="forms-controls">
        <select data-testid="forms-deal-select" value={dealId} onChange={(e) => { setDealId(e.target.value); setReady(false); setValues({}); }}
          className="bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 min-w-[280px]">
          <option value="">Select a federal deal…</option>
          {deals.map((d) => <option key={d.id} value={d.id}>{d.solicitation_number || d.est_code} — {d.title}</option>)}
        </select>
        <span className="text-xs text-zinc-400 font-mono">Form: SF-1442</span>
        <button data-testid="forms-ai-map" onClick={aiMap} disabled={busy || !dealId}
          className="flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-zinc-800 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-gold" />} AI auto-map with Anvil
        </button>
      </div>

      {Object.keys(values).length > 0 && (
        <div className="mt-5 grid lg:grid-cols-2 gap-5 items-start">
          <div className="bg-white border border-zinc-200 shadow-sm rounded-xl p-4" data-testid="forms-fields">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><AnvilIcon className="h-4 w-4" /> SF-1442 offeror fields</h2>
              <button data-testid="forms-save-preview" onClick={saveAndPreview} disabled={busy}
                className="flex items-center gap-1.5 text-xs bg-gold-dark text-white px-3 py-1.5 rounded-md hover:opacity-90 disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Save & preview</button>
            </div>
            <div className="space-y-2.5">
              {visKeys.map((k) => (
                <label key={k} className="block">
                  <span className="text-[11px] text-zinc-500 flex items-center gap-1.5">{LABELS[k] || k}
                    {aiKeys.includes(k) && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gold/20 text-gold-dark font-semibold">AI</span>}</span>
                  <textarea data-testid={`forms-field-${k}`} value={values[k] || ""} rows={k === "company_name_address" || k === "remittance_address" ? 3 : 1}
                    onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                    className="w-full mt-0.5 bg-white border border-zinc-300 rounded px-2 py-1.5 text-sm text-zinc-900 outline-none focus:border-gold-dark resize-none" />
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white border border-zinc-200 shadow-sm rounded-xl p-4 flex flex-col" data-testid="forms-preview" style={{ minHeight: "70vh" }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-zinc-900 font-mono">Filled SF-1442 preview</div>
              {ready && (
                <div className="flex items-center gap-2">
                  <a href={pdfUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900" data-testid="forms-open"><ExternalLink className="h-3.5 w-3.5" /> Open</a>
                  <a href={pdfUrl} download className="flex items-center gap-1 text-[11px] bg-zinc-900 text-white px-2.5 py-1 rounded-md hover:bg-zinc-800" data-testid="forms-download"><Download className="h-3.5 w-3.5" /> Download</a>
                </div>
              )}
            </div>
            {ready ? (
              <iframe title="SF-1442" src={pdfUrl} className="flex-1 w-full min-h-[60vh] border border-zinc-200 rounded-md" data-testid="forms-iframe" />
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-zinc-400">Edit fields, then Save & preview to render the filled form.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
